'use client';

import { FacebookPixel } from './FacebookPixel';
import { LinkedInPixel } from './LinkedInPixel';

// Componente principal que renderiza todos los pixels activos
export function PixelManager() {
  return (
    <>
      <FacebookPixel />
      <LinkedInPixel />
    </>
  );
}

// Interfaz base para eventos de tracking
interface TrackingData {
  [key: string]: any;
}

// Estrategia base para los pixels
interface PixelStrategy {
  isEnabled: () => boolean;
  trackEvent: (eventName: string, data?: TrackingData) => void;
}

// Estrategia de Facebook Pixel
class FacebookPixelStrategy implements PixelStrategy {
  isEnabled(): boolean {
    return !!(
      typeof window !== 'undefined' &&
      window.fbq &&
      process.env.NEXT_PUBLIC_FB_PIXEL_ID
    );
  }

  trackEvent(eventName: string, data?: TrackingData): void {
    if (this.isEnabled()) {
      window.fbq('track', eventName, data);
    }
  }

  trackCustomEvent(eventName: string, data?: TrackingData): void {
    if (this.isEnabled()) {
      window.fbq('trackCustom', eventName, data);
    }
  }
}

// Estrategia de LinkedIn Pixel
class LinkedInPixelStrategy implements PixelStrategy {
  isEnabled(): boolean {
    return !!(
      typeof window !== 'undefined' &&
      window.lintrk &&
      process.env.NEXT_PUBLIC_LINKEDIN_PARTNER_ID
    );
  }

  trackEvent(eventName: string, data?: TrackingData): void {
    if (this.isEnabled() && data?.conversion_id) {
      window.lintrk('track', { conversion_id: data.conversion_id });
    }
  }
}

// Manager central que coordina todos los pixels
class PixelTrackingManager {
  private strategies: Map<string, PixelStrategy> = new Map();

  constructor() {
    this.strategies.set('facebook', new FacebookPixelStrategy());
    this.strategies.set('linkedin', new LinkedInPixelStrategy());
  }

  // Ejecuta un evento en todos los pixels activos
  track(eventName: string, data?: TrackingData): void {
    this.strategies.forEach((strategy, name) => {
      if (strategy.isEnabled()) {
        try {
          strategy.trackEvent(eventName, data);
        } catch (error) {
          console.warn(`Error tracking event ${eventName} on ${name}:`, error);
        }
      }
    });
  }

  // Ejecuta un evento solo en pixels específicos
  trackOn(pixels: string[], eventName: string, data?: TrackingData): void {
    pixels.forEach((pixelName) => {
      const strategy = this.strategies.get(pixelName);
      if (strategy?.isEnabled()) {
        try {
          strategy.trackEvent(eventName, data);
        } catch (error) {
          console.warn(`Error tracking event ${eventName} on ${pixelName}:`, error);
        }
      }
    });
  }

  // Ejecuta un evento custom solo en Facebook
  trackCustomFacebook(eventName: string, data?: TrackingData): void {
    const fbStrategy = this.strategies.get('facebook') as FacebookPixelStrategy;
    if (fbStrategy?.isEnabled()) {
      fbStrategy.trackCustomEvent(eventName, data);
    }
  }

  // Verifica si algún pixel está activo
  hasActivePixels(): boolean {
    return Array.from(this.strategies.values()).some((strategy) =>
      strategy.isEnabled()
    );
  }

  // Obtiene la lista de pixels activos
  getActivePixels(): string[] {
    return Array.from(this.strategies.entries())
      .filter(([_, strategy]) => strategy.isEnabled())
      .map(([name]) => name);
  }
}

// Instancia singleton del manager
const pixelManager = new PixelTrackingManager();

// Eventos predefinidos unificados para todos los pixels
export const PixelEvents = {
  // ==================== FORMULARIO DE CONTACTO ====================
  
  startContactForm: () => {
    pixelManager.trackCustomFacebook('StartContactForm', {
      content_category: 'Lead Generation',
    });
  },

  progressContactForm: (step: number, totalSteps: number) => {
    pixelManager.trackCustomFacebook('ContactFormProgress', {
      step: step,
      total_steps: totalSteps,
      progress_percentage: Math.round((step / totalSteps) * 100),
    });
  },

  submitContactForm: (formData?: {
    services?: string[];
    country?: string;
    linkedinConversionId?: string;
  }) => {
    // Facebook: Evento Lead estándar
    pixelManager.trackOn(['facebook'], 'Lead', {
      content_name: 'Contact Form',
      content_category: 'Lead Generation',
      value: 1,
      currency: 'USD',
      services: formData?.services,
      country: formData?.country,
    });

    // LinkedIn: Conversión (si se proporciona conversion_id)
    if (formData?.linkedinConversionId) {
      pixelManager.trackOn(['linkedin'], 'conversion', {
        conversion_id: formData.linkedinConversionId,
      });
    }
  },

  // ==================== POSTULACIÓN LABORAL ====================

  startJobApplication: (position?: string) => {
    pixelManager.trackCustomFacebook('StartJobApplication', {
      content_category: 'Recruitment',
      position: position || 'General',
    });
  },

  uploadCV: (position: string) => {
    pixelManager.trackCustomFacebook('UploadCV', {
      position: position,
    });
  },

  submitJobApplication: (
    position: string,
    expectedIncome?: string,
    linkedinConversionId?: string
  ) => {
    // Facebook: Evento SubmitApplication
    pixelManager.trackOn(['facebook'], 'SubmitApplication', {
      content_name: 'Job Application',
      content_category: 'Recruitment',
      position: position,
      expected_income: expectedIncome,
    });

    // LinkedIn: Conversión (si se proporciona conversion_id)
    if (linkedinConversionId) {
      pixelManager.trackOn(['linkedin'], 'conversion', {
        conversion_id: linkedinConversionId,
      });
    }
  },

  // ==================== BLOG & CONTENIDO ====================

  viewBlogPost: (postTitle: string, category?: string, author?: string) => {
    pixelManager.trackOn(['facebook'], 'ViewContent', {
      content_name: postTitle,
      content_type: 'blog_post',
      content_category: category || 'Blog',
      author: author,
    });
  },

  readBlogPost: (postTitle: string, timeSpent: number) => {
    pixelManager.trackCustomFacebook('ReadBlogPost', {
      content_name: postTitle,
      time_spent_seconds: timeSpent,
      engaged: timeSpent > 30,
    });
  },

  searchBlog: (searchQuery: string, resultsCount: number = 0) => {
    pixelManager.trackOn(['facebook'], 'Search', {
      search_string: searchQuery,
      content_category: 'Blog',
      results_count: resultsCount,
    });
  },

  filterBlogByCategory: (category: string) => {
    pixelManager.trackCustomFacebook('FilterBlogCategory', {
      category: category,
    });
  },

  // ==================== PROYECTOS ====================

  viewProject: (projectName: string, category?: string) => {
    pixelManager.trackOn(['facebook'], 'ViewContent', {
      content_name: projectName,
      content_type: 'case_study',
      content_category: category || 'Projects',
    });
  },

  // ==================== SERVICIOS ====================

  viewService: (serviceName: string) => {
    pixelManager.trackOn(['facebook'], 'ViewContent', {
      content_name: serviceName,
      content_type: 'service',
      content_category: 'Services',
    });
  },

  requestServiceQuote: (serviceName: string, linkedinConversionId?: string) => {
    pixelManager.trackCustomFacebook('RequestServiceQuote', {
      service: serviceName,
      content_category: 'Lead Generation',
    });

    if (linkedinConversionId) {
      pixelManager.trackOn(['linkedin'], 'conversion', {
        conversion_id: linkedinConversionId,
      });
    }
  },

  // ==================== SOCIAL MEDIA ====================

  clickSocialLink: (platform: string, location: string) => {
    pixelManager.trackCustomFacebook('SocialClick', {
      platform: platform,
      location: location,
    });
  },

  clickLinkedInProfile: (profileName: string, memberRole?: string) => {
    pixelManager.trackCustomFacebook('LinkedInProfileClick', {
      profile_name: profileName,
      member_role: memberRole,
      platform: 'LinkedIn',
    });
  },

  // ==================== NAVEGACIÓN & CTAs ====================

  clickCTA: (ctaText: string, location: string, destination?: string) => {
    pixelManager.trackCustomFacebook('CTAClick', {
      cta_text: ctaText,
      location: location,
      destination: destination,
    });
  },

  scrollToSection: (sectionName: string) => {
    pixelManager.trackCustomFacebook('ScrollToSection', {
      section: sectionName,
    });
  },

  // ==================== RECURSOS & DESCARGAS ====================

  downloadResource: (
    resourceName: string,
    resourceType: string,
    linkedinConversionId?: string
  ) => {
    pixelManager.trackCustomFacebook('Download', {
      resource_name: resourceName,
      resource_type: resourceType,
    });

    if (linkedinConversionId) {
      pixelManager.trackOn(['linkedin'], 'conversion', {
        conversion_id: linkedinConversionId,
      });
    }
  },

  // ==================== VIDEO ====================

  playVideo: (videoTitle: string, location: string) => {
    pixelManager.trackCustomFacebook('VideoPlay', {
      video_title: videoTitle,
      location: location,
    });
  },

  // ==================== ENGAGEMENT ====================

  timeOnPage: (pageName: string, timeSpent: number) => {
    if (timeSpent > 10) {
      pixelManager.trackCustomFacebook('EngagedTimeOnPage', {
        page_name: pageName,
        time_spent_seconds: timeSpent,
        engaged: timeSpent > 30,
      });
    }
  },

  // ==================== NEWSLETTER ====================

  subscribeNewsletter: (linkedinConversionId?: string) => {
    pixelManager.trackOn(['facebook'], 'Lead', {
      content_name: 'Newsletter Subscription',
      content_category: 'Newsletter',
    });

    if (linkedinConversionId) {
      pixelManager.trackOn(['linkedin'], 'conversion', {
        conversion_id: linkedinConversionId,
      });
    }
  },
};

// Exportar también el manager para casos de uso avanzados
export { pixelManager };
